// Body map field logic. Region IDs are a contract with the native app
// (help-from-sky-native/src/domain/forms/bodyMap.ts) and with every stored
// response — never rename one. Each list runs top to bottom, which is also the
// order selections are listed and submitted in.
import type { BodyMapSelection, BodyMapView, FormAnswer, FormFieldDefinition } from '../types/forms';

export const BODY_MAP_VIEWS: readonly BodyMapView[] = ['front', 'back'];
export const BODY_MAP_NOTE_MAX = 200;

export const BODY_MAP_REGIONS: Record<BodyMapView, readonly string[]> = {
  front: [
    'head', 'neck',
    'left_shoulder', 'right_shoulder',
    'chest',
    'left_upper_arm', 'right_upper_arm',
    'abdomen',
    'left_forearm', 'right_forearm',
    'hips',
    'left_hand', 'right_hand',
    'left_thigh', 'right_thigh',
    'left_knee', 'right_knee',
    'left_lower_leg', 'right_lower_leg',
    'left_foot', 'right_foot',
  ],
  back: [
    'head', 'neck',
    'left_shoulder_blade', 'right_shoulder_blade',
    'upper_back',
    'left_upper_arm', 'right_upper_arm',
    'mid_back',
    'left_forearm', 'right_forearm',
    'lower_back',
    'left_hand', 'right_hand',
    'left_glute', 'right_glute',
    'left_hamstring', 'right_hamstring',
    'left_calf', 'right_calf',
    'left_foot', 'right_foot',
  ],
};

export const BODY_MAP_VIEW_LABELS: Record<BodyMapView, string> = { front: 'Front', back: 'Back' };

const REGION_LABELS: Record<string, string> = {
  head: 'Head',
  neck: 'Neck',
  chest: 'Chest',
  abdomen: 'Abdomen',
  hips: 'Hips',
  upper_back: 'Upper back',
  mid_back: 'Mid back',
  lower_back: 'Lower back',
};

/** "left_shoulder_blade" → "Left shoulder blade". */
export function bodyMapRegionLabel(regionId: string): string {
  if (REGION_LABELS[regionId]) return REGION_LABELS[regionId];
  const words = regionId.split('_').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface BodyMapConfig {
  views: BodyMapView[];
  single: boolean;
  notes: boolean;
}

/** The field's settings, with defaults for anything missing. */
export function bodyMapConfig(field: FormFieldDefinition): BodyMapConfig {
  const views = BODY_MAP_VIEWS.filter(v => field.bodyMapViews?.includes(v));
  return {
    views: views.length > 0 ? views : [...BODY_MAP_VIEWS],
    single: field.bodyMapSelection === 'single',
    notes: field.bodyMapNotes ?? true,
  };
}

const orderOf = (s: Pick<BodyMapSelection, 'view' | 'regionId'>) =>
  BODY_MAP_VIEWS.indexOf(s.view) * 100 + BODY_MAP_REGIONS[s.view].indexOf(s.regionId);

/** Front regions first, then back, each top to bottom. */
export function sortBodyMapSelections(selections: BodyMapSelection[]): BodyMapSelection[] {
  return [...selections].sort((a, b) => orderOf(a) - orderOf(b));
}

export const isBodyMapSelected = (selections: BodyMapSelection[], view: BodyMapView, regionId: string) =>
  selections.some(s => s.view === view && s.regionId === regionId);

/**
 * Taps a region: clears it if selected, otherwise adds it — or, in single
 * mode, replaces whatever was selected on either view.
 */
export function toggleBodyMapRegion(
  selections: BodyMapSelection[],
  view: BodyMapView,
  regionId: string,
  single: boolean,
): BodyMapSelection[] {
  if (isBodyMapSelected(selections, view, regionId)) {
    return selections.filter(s => !(s.view === view && s.regionId === regionId));
  }
  const added = { regionId, view, notes: '' };
  return single ? [added] : sortBodyMapSelections([...selections, added]);
}

/** Builds the submitted answer: trimmed notes (dropped when notes are off) plus a plain-text summary. */
export function bodyMapAnswer(selections: BodyMapSelection[], notesEnabled: boolean): FormAnswer {
  const clean = sortBodyMapSelections(selections).map(s => ({
    regionId: s.regionId,
    view: s.view,
    notes: notesEnabled ? s.notes.trim().slice(0, BODY_MAP_NOTE_MAX) : '',
  }));
  const value = clean
    .map(s => {
      const label = `${bodyMapRegionLabel(s.regionId)} (${s.view})`;
      return s.notes ? `${label}: ${s.notes}` : label;
    })
    .join('; ');
  return { type: 'text', value, selections: clean };
}
